if (!customElements.get('icebox-accordion')) {
  customElements.define(
    'icebox-accordion',
    class IceboxAccordion extends HTMLElement {
      connectedCallback() {
        this.details = this.querySelector('details');
        if (!this.details) return;

        this.summary = this.details.querySelector('summary');
        this.content = this.details.querySelector('.icebox-accordion__content');
        if (!this.summary || !this.content) return;

        // Native toggles (reduced-motion clicks skip the animated path, plus
        // browser-initiated opens like find-in-page) must still sync ARIA.
        if (!this.toggleSyncBound) {
          this.onDetailsToggle = () => {
            this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
          };
          this.details.addEventListener('toggle', this.onDetailsToggle);
          this.toggleSyncBound = true;
        }

        this.isFooterAccordion = this.classList.contains('fcust__accordion');
        this.desktopFooterQuery = window.matchMedia('(min-width: 990px)');
        this.onDesktopFooterChange = this.onDesktopFooterChange.bind(this);

        if (this.isFooterAccordion) {
          this.desktopFooterQuery.addEventListener('change', this.onDesktopFooterChange);
          this.syncFooterDesktopState();
          if (this.desktopFooterQuery.matches) return;
        }

        this.enableAccordionEnhancement();
      }

      disconnectedCallback() {
        if (this.summaryClickBound) {
          this.summary.removeEventListener('click', this.onSummaryClick);
          // Clear the flag so enableAccordionEnhancement re-binds on reconnect.
          this.summaryClickBound = false;
        }
        if (this.toggleSyncBound) {
          this.details.removeEventListener('toggle', this.onDetailsToggle);
          this.toggleSyncBound = false;
        }
        this.desktopFooterQuery?.removeEventListener('change', this.onDesktopFooterChange);
        this.clearTransitionListeners();
      }

      enableAccordionEnhancement() {
        if (this.summaryClickBound) return;

        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.onSummaryClick = this.onSummaryClick.bind(this);
        this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
        this.summary.addEventListener('click', this.onSummaryClick);
        this.summaryClickBound = true;
        this.classList.add('is-enhanced');
      }

      syncFooterDesktopState() {
        if (!this.isFooterAccordion) return;

        if (this.desktopFooterQuery.matches) {
          this.details.setAttribute('open', '');
          this.summary.setAttribute('aria-expanded', 'true');
          this.classList.remove('is-enhanced');
          this.resetContentStyles();
          this.clearTransitionListeners();
          if (this.summaryClickBound) {
            this.summary.removeEventListener('click', this.onSummaryClick);
            this.summaryClickBound = false;
          }
          return;
        }

        this.details.removeAttribute('open');
        this.summary.setAttribute('aria-expanded', 'false');
        this.resetContentStyles();
        this.enableAccordionEnhancement();
      }

      onDesktopFooterChange() {
        this.syncFooterDesktopState();
      }

      clearTransitionListeners() {
        if (this.openFallbackTimer) {
          window.clearTimeout(this.openFallbackTimer);
          this.openFallbackTimer = null;
        }
        if (this.closeFallbackTimer) {
          window.clearTimeout(this.closeFallbackTimer);
          this.closeFallbackTimer = null;
        }
        this.content?.removeEventListener('transitionend', this.onOpenTransitionEnd);
        this.content?.removeEventListener('transitionend', this.onCloseTransitionEnd);
      }

      forceReflow() {
        void this.content.offsetHeight;
      }

      getTransitionDurationMs() {
        const duration = window.getComputedStyle(this.content).transitionDuration;
        const durations = duration.split(',').map((value) => {
          const trimmed = value.trim();
          return trimmed.endsWith('ms') ? parseFloat(trimmed) : parseFloat(trimmed) * 1000;
        });
        return Math.max(...durations, 0);
      }

      resetContentStyles() {
        this.content.style.height = '';
        this.content.style.overflow = '';
      }

      // Accordions tagged with a matching data-accordion-group behave as one
      // stack: opening one collapses the rest. Peers are looked up from the
      // root rather than among siblings because group members don't always
      // share a parent — the PDP specs drawer sits inside its own
      // <product-spec-card> host.
      closeGroupPeers() {
        const group = this.dataset.accordionGroup;
        if (!group) return;

        const selector = `icebox-accordion[data-accordion-group="${CSS.escape(group)}"]`;
        this.getRootNode()
          .querySelectorAll(selector)
          .forEach((peer) => {
            if (peer !== this) peer.collapse?.();
          });
      }

      // Collapse driven by a group peer opening rather than by a click. Routes
      // through the same animation and ARIA updates as a user-driven close.
      collapse() {
        if (!this.details?.open) return;

        if (!this.classList.contains('is-enhanced') || this.motionQuery?.matches) {
          this.details.removeAttribute('open');
          this.summary?.setAttribute('aria-expanded', 'false');
          return;
        }

        this.clearTransitionListeners();
        this.close();
      }

      onSummaryClick(event) {
        if (this.motionQuery.matches) {
          // Native <details> owns the toggle here; details.open still holds the
          // pre-click value, so a closed drawer is the one about to open.
          if (!this.details.open) this.closeGroupPeers();
          return;
        }

        event.preventDefault();
        this.clearTransitionListeners();

        if (this.details.open) {
          this.close();
        } else {
          this.closeGroupPeers();
          this.open();
        }
      }

      open() {
        this.details.setAttribute('open', '');
        this.summary.setAttribute('aria-expanded', 'true');
        this.content.style.overflow = 'hidden';
        this.content.style.height = '0px';
        this.forceReflow();

        window.requestAnimationFrame(() => {
          this.content.style.height = `${this.content.scrollHeight}px`;
        });

        this.content.addEventListener('transitionend', this.onOpenTransitionEnd);
        this.openFallbackTimer = window.setTimeout(() => {
          this.finishOpen();
        }, this.getTransitionDurationMs() + 50);
      }

      finishOpen() {
        if (!this.details.open) return;

        this.resetContentStyles();
        this.clearTransitionListeners();
      }

      onOpenTransitionEnd = (event) => {
        if (event.target !== this.content || event.propertyName !== 'height') return;
        this.finishOpen();
      };

      close() {
        const startHeight = this.content.scrollHeight;
        this.content.style.overflow = 'hidden';
        this.content.style.height = `${startHeight}px`;
        this.forceReflow();

        window.requestAnimationFrame(() => {
          this.content.style.height = '0px';
        });

        this.content.addEventListener('transitionend', this.onCloseTransitionEnd);
        this.closeFallbackTimer = window.setTimeout(() => {
          this.finishClose();
        }, this.getTransitionDurationMs() + 50);
      }

      finishClose() {
        this.details.removeAttribute('open');
        this.summary.setAttribute('aria-expanded', 'false');
        this.resetContentStyles();
        this.clearTransitionListeners();
      }

      onCloseTransitionEnd = (event) => {
        if (event.target !== this.content || event.propertyName !== 'height') return;
        this.finishClose();
      };
    }
  );
}
